import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { unreachableCaseError } from 'types';
import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Toast as ToastComponent } from './Toast';
import type { ToastProps, ToastKey, ToastPosition } from './Toast';
import { nanoid } from 'nanoid';
import { ToastContainer } from './ToastContainer';
import { EMPTY } from 'config/constants';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useIsAuthenticated } from 'hooks/useAuth';
import usePrevious from 'react-use/lib/usePrevious';

export type Toast = ToastProps;

export type ToasterState = Readonly<{
  toasts: Toast[];
  queue: Toast[];
  maxNumberOfVisibleToasts: number;
}>;

export type EnqueueToastFn = (
  msg: React.ReactNode,
  options?: Readonly<Partial<Toast>>
) => Nullable<ToastKey>;

export type EnqueueOrUpdateToastFn = (
  msg: React.ReactNode,
  toastKey?: Nullable<ToastKey>,
  options?: Readonly<Partial<Toast>>
) => Nullable<ToastKey>;

export type ToasterDispatch = Readonly<{
  enqueueToast: EnqueueToastFn;
  enqueueOrUpdateToast: EnqueueOrUpdateToastFn;
  closeToast: (toastKey: ToastKey) => void;
}>;

const ToasterStateContext = createContext<Nullable<ToasterState>>(undefined);
const ToasterDispatchContext = createContext<Nullable<ToasterDispatch>>(undefined);

export type ToasterProviderProps = Readonly<{
  children: React.ReactNode;
  /**
   * Number of toasts to show visible on the screen at one time.
   */
  maxNumberOfVisibleToasts?: number;
  /**
   * Runs the animations on mount of the toast containers. You want this on
   * unless you are running visual tests.
   */
  runAnimationsOnMount?: boolean;
  /**
   * Initial toasts to show in the app. Useful for snapshot testing.
   */
  initialToasts?: Toast[];
  /**
   * Initial items to place in the queue. Useful for testing.
   */
  initialQueue?: Toast[];
  /**
   * Default toast props that will be deeply merged with all toasts created via the hook.
   */
  defaultToastProps?: Readonly<Partial<Toast>>;
}>;

type PendingToast = Readonly<{
  message: React.ReactNode;
  toastKey: ToastKey;
  options?: Readonly<Partial<Toast>>;
}>;

type ToastDecision = 'show' | 'hide' | 'defer';

type ActionType =
  | Readonly<{
      type: 'enqueueToast';
      payload: {
        toast: Toast;
      };
    }>
  | Readonly<{
      type: 'closeToast';
      payload: {
        toastKey: string;
      };
    }>
  | Readonly<{
      type: 'updateMaxNumberOfVisibleToasts';
      payload: {
        maxNumberOfVisibleToasts: number;
      };
    }>
  | Readonly<{
      type: 'enqueueOrUpdateToast';
      payload: {
        toast: Toast;
      };
    }>;

const enqueueToastState = (state: ToasterState, toast: Toast): ToasterState => {
  const toastAlreadyExists =
    toast.isUnique === true && state.toasts.some((existing) => existing.message === toast.message);

  if (toastAlreadyExists) {
    return state;
  }

  const toasts = [...state.toasts];
  const queue = [...state.queue];

  const toastIdx = toasts.findIndex((existing) => existing.toastKey === toast.toastKey);
  if (toastIdx !== -1) {
    toasts.splice(toastIdx, 1, toast);
  } else if (toasts.length < state.maxNumberOfVisibleToasts) {
    toasts.push(toast);
  } else {
    queue.push(toast);
  }

  return {
    ...state,
    toasts,
    queue,
  };
};

const upsertPendingToast = (pendingToasts: PendingToast[], next: PendingToast): PendingToast[] => {
  const pendingToastIndex = pendingToasts.findIndex((toast) => toast.toastKey === next.toastKey);

  if (pendingToastIndex === -1) {
    return [...pendingToasts, next];
  }

  const nextPendingToasts = [...pendingToasts];
  nextPendingToasts[pendingToastIndex] = next;
  return nextPendingToasts;
};

const toasterReducer = (state: ToasterState, action: ActionType): ToasterState => {
  switch (action.type) {
    case 'enqueueToast':
      return enqueueToastState(state, action.payload.toast);
    case 'enqueueOrUpdateToast': {
      const { toast } = action.payload;
      const toastIndex = state.toasts.findIndex((existing) => existing.toastKey === toast.toastKey);

      if (toastIndex !== -1) {
        const updatedToast = {
          ...state.toasts[toastIndex],
          ...toast,
        } as const;

        const toasts = [...state.toasts];
        toasts[toastIndex] = updatedToast;

        return {
          ...state,
          toasts,
        };
      }

      return enqueueToastState(state, toast);
    }
    case 'closeToast':
      const { toastKey } = action.payload;

      // Determine whether the toast being closed is currently visible.
      // This affects whether we should promote a queued toast.
      const wasVisible = state.toasts.some((t) => t.toastKey === toastKey);

      // Remove the toast from the list of visible toasts.
      const visibleAfterRemoval = state.toasts.filter((t) => t.toastKey !== toastKey);

      // Remove the toast from the queue as well, in case it was queued but not visible.
      const queueAfterRemoval = state.queue.filter((t) => t.toastKey !== toastKey);

      let nextToasts = visibleAfterRemoval;
      let nextQueue = queueAfterRemoval;

      /**
       * If closing a toast creates space AND we still have queued toasts,
       * promote the next toast from the queue into the visible list.
       */
      if (visibleAfterRemoval.length < state.maxNumberOfVisibleToasts && state.queue.length > 0) {
        const nextInLine = state.queue[0];
        if (nextInLine) {
          nextToasts = [...visibleAfterRemoval, nextInLine];
        }
      }

      /**
       * If the toast being closed was actually visible,
       * we pop one toast off the queue (from the end)
       */
      if (wasVisible && state.queue.length > 0) {
        nextQueue = queueAfterRemoval.slice(0, -1);
      }

      return {
        ...state,
        toasts: nextToasts,
        queue: nextQueue,
      };

    case 'updateMaxNumberOfVisibleToasts':
      return {
        ...state,
        maxNumberOfVisibleToasts: action.payload.maxNumberOfVisibleToasts,
      };

    default:
      return unreachableCaseError((action as ActionType).type);
  }
};

export const ToasterProvider = ({
  children,
  maxNumberOfVisibleToasts = 1000,
  defaultToastProps,
  initialToasts = EMPTY.ARRAY,
  initialQueue = EMPTY.ARRAY,
}: ToasterProviderProps): React.ReactElement => {
  const isAuthenticated = useIsAuthenticated();
  const { data, loading } = useCurrentUser({ skip: !isAuthenticated });
  const notificationSettings = useMemo(() => {
    return data?.me?.generalSettings?.notifications;
  }, [data?.me?.generalSettings?.notifications]);

  const defaultToastPropsWithSettings = useMemo(() => {
    return {
      ...defaultToastProps,
      autoHideDuration: notificationSettings?.duration ?? defaultToastProps?.autoHideDuration,
    };
  }, [defaultToastProps, notificationSettings?.duration]);

  const [state, dispatch] = useReducer(toasterReducer, {
    toasts: initialToasts,
    queue: initialQueue,
    maxNumberOfVisibleToasts,
  });
  const [pendingToasts, setPendingToasts] = useState<PendingToast[]>([]);

  useEffect(() => {
    if (maxNumberOfVisibleToasts !== state.maxNumberOfVisibleToasts) {
      dispatch({
        type: 'updateMaxNumberOfVisibleToasts',
        payload: { maxNumberOfVisibleToasts },
      });
    }
  }, [maxNumberOfVisibleToasts, state.maxNumberOfVisibleToasts, dispatch]);

  const getToastDecision = useCallback(
    (options?: Readonly<Partial<Toast>>) => {
      const isErrorLikeToast = options?.severity === 'error' || options?.icon === 'redWarning';

      if (!isAuthenticated) {
        return isErrorLikeToast ? 'show' : 'hide';
      }

      if (loading) {
        return isErrorLikeToast ? 'show' : 'defer';
      }

      const visibilityMode = notificationSettings?.visibilityMode ?? 'all';

      if (visibilityMode === 'all') {
        return 'show';
      }

      return isErrorLikeToast ? 'show' : 'hide';
    },
    [isAuthenticated, loading, notificationSettings]
  );

  const buildToastPayload = useCallback(
    (message: React.ReactNode, toastKey: ToastKey, options?: Readonly<Partial<Toast>>) => {
      const baseToast: Toast = {
        position: 'top-right',
        ...defaultToastPropsWithSettings,
        toastKey,
        message,
        autoHideDuration:
          options?.autoHideDuration ?? defaultToastPropsWithSettings.autoHideDuration,
      };

      return {
        ...baseToast,
        ...options,
      };
    },
    [defaultToastPropsWithSettings]
  );

  const previousIsAuthenticated = usePrevious(isAuthenticated);

  // Clear pending toasts on logout (true → false), not during initial load,
  // so toasts enqueued before auth resolves aren't silently dropped.
  useEffect(() => {
    if (!isAuthenticated && previousIsAuthenticated) {
      setPendingToasts([]);
    }
  }, [isAuthenticated, previousIsAuthenticated]);

  // Flush pending toasts once auth and settings are ready.
  useEffect(() => {
    if (!isAuthenticated || loading || pendingToasts.length === 0) {
      return;
    }

    pendingToasts.forEach(({ message, toastKey, options }) => {
      if (getToastDecision(options) !== 'show') {
        return;
      }

      dispatch({
        type: 'enqueueOrUpdateToast',
        payload: {
          toast: buildToastPayload(message, toastKey, options),
        },
      });
    });

    // Use a Set to avoid clobbering toasts enqueued during this render cycle.
    const processedKeys = new Set(pendingToasts.map((t) => t.toastKey));
    setPendingToasts((current) => current.filter((t) => !processedKeys.has(t.toastKey)));
  }, [buildToastPayload, dispatch, getToastDecision, isAuthenticated, loading, pendingToasts]);

  const enqueueToast = useCallback(
    (message: React.ReactNode, options?: Readonly<Partial<Toast>>) => {
      const toastKey = nanoid();
      const decision = getToastDecision(options);

      if (decision === 'hide') {
        return null;
      }

      if (decision === 'defer') {
        setPendingToasts((currentPendingToasts) =>
          upsertPendingToast(currentPendingToasts, { message, toastKey, options })
        );
        return toastKey;
      }

      const toast = buildToastPayload(message, toastKey, options);

      dispatch({
        type: 'enqueueToast',
        payload: {
          toast,
        },
      });

      return toastKey;
    },
    [buildToastPayload, getToastDecision]
  );

  const enqueueOrUpdateToast = useCallback(
    (message: React.ReactNode, toastKey?: ToastKey | null, options?: Readonly<Partial<Toast>>) => {
      if (toastKey != null) {
        const decision = getToastDecision(options);

        if (decision === 'hide') {
          setPendingToasts((currentPendingToasts) =>
            currentPendingToasts.filter((toast) => toast.toastKey !== toastKey)
          );
          return null;
        }

        if (decision === 'defer') {
          setPendingToasts((currentPendingToasts) =>
            upsertPendingToast(currentPendingToasts, { message, toastKey, options })
          );
          return toastKey;
        }

        const toast = buildToastPayload(message, toastKey, options);
        dispatch({
          type: 'enqueueOrUpdateToast',
          payload: { toast },
        });
        return toastKey;
      }

      return enqueueToast(message, options);
    },
    [buildToastPayload, dispatch, enqueueToast, getToastDecision]
  );

  const closeToast = useCallback(
    (toastKey: ToastKey) => {
      setPendingToasts((currentPendingToasts) =>
        currentPendingToasts.filter((toast) => toast.toastKey !== toastKey)
      );
      dispatch({ type: 'closeToast', payload: { toastKey } });
    },
    [dispatch]
  );

  const toastContainers = useMemo(() => {
    // Group toasts by position, defaulting to "top-right"
    const grouped = state.toasts.reduce<Record<ToastPosition, Toast[]>>(
      (acc, toast) => {
        const position = toast.position ?? 'top-right';

        if (!acc[position]) {
          acc[position] = [];
        }
        acc[position].push(toast);
        return acc;
      },
      {} as Record<ToastPosition, Toast[]>
    );

    return (Object.entries(grouped) as [ToastPosition, Toast[]][]).map(([position, toasties]) => (
      <ToastContainer position={position} key={position}>
        {toasties.map((toast) => {
          const { toastKey, message, ...toastProps } = toast;
          return (
            <ToastComponent
              key={toastKey}
              toastKey={toastKey}
              message={message}
              onClose={() => dispatch({ type: 'closeToast', payload: { toastKey } })}
              {...toastProps}
            />
          );
        })}
      </ToastContainer>
    ));
  }, [state.toasts, dispatch]);

  const toasterDispatchBag = useMemo(
    () => ({
      enqueueToast,
      enqueueOrUpdateToast,
      closeToast,
    }),
    [enqueueToast, enqueueOrUpdateToast, closeToast]
  );

  return (
    <ToasterStateContext.Provider value={state}>
      <ToasterDispatchContext.Provider value={toasterDispatchBag}>
        {children}
        {typeof document === 'undefined'
          ? null
          : createPortal(<AnimatePresence>{toastContainers}</AnimatePresence>, document.body)}
      </ToasterDispatchContext.Provider>
    </ToasterStateContext.Provider>
  );
};

export const useToasterState = (): ToasterState => {
  const context = useContext(ToasterStateContext);

  if (!context) {
    throw new Error('useToasterState must be used within a ToasterProvider.');
  }

  return context;
};

export const useToasterDispatch = (): ToasterDispatch => {
  const context = useContext(ToasterDispatchContext);

  if (!context) {
    throw new Error('useToasterDispatch must be used within a ToasterProvider.');
  }

  return context;
};

export const useToaster = (): [ToasterState, ToasterDispatch] => {
  const stateContext = useContext(ToasterStateContext);
  const dispatchContext = useContext(ToasterDispatchContext);

  if (!stateContext || !dispatchContext) {
    throw new Error('useToaster must be used within a ToasterProvider.');
  }

  return [stateContext, dispatchContext];
};
